import {
  getClassroomTransitionPreflight,
  getClassroomTransitionReconciliation,
  issueClassroomTransitionHandoff,
  saveClassroomTransitionScope,
  submitClassroomTransitionTeacherNomination,
} from "../../../services/APIClient";
import {
  DIFFERENT_GROUPINGS_PROGRESS,
  createEmptyDifferentGroupingsTask,
  isValidClassroomCount,
  normalizeConfiguredProgressionPoints,
} from "./differentGroupingsContract";

const pointProgress = (point) => {
  if (point.reconciliationStatus === "completed") return DIFFERENT_GROUPINGS_PROGRESS.RELEASED;
  if (Number(point.confirmedCount) > 0 || point.currentDraft || point.setupAssignments?.length) {
    return DIFFERENT_GROUPINGS_PROGRESS.INFORMATION_RECEIVED;
  }
  return DIFFERENT_GROUPINGS_PROGRESS.INFORMATION_REQUIRED;
};

const isIssuedHandoff = (handoff) =>
  typeof handoff?.code === "string" && handoff.code.trim() !== "" &&
  typeof handoff?.expiresAt === "string" && !Number.isNaN(Date.parse(handoff.expiresAt)) &&
  typeof handoff?.handoffAttempt?.id === "string" && handoff.handoffAttempt.id.trim() !== "" &&
  handoff.handoffAttempt.status === "issued" &&
  typeof handoff.handoffAttempt.createdAt === "string" &&
  !Number.isNaN(Date.parse(handoff.handoffAttempt.createdAt));

const SETUP_ASSIGNMENT_STATUSES = new Set(["setup_required", "in_progress"]);
const SETUP_ASSIGNMENT_STATE_VALIDATORS = Object.freeze({
  not_started: ({ assignments }) => assignments.length === 0,
  in_progress: ({ assignments, outstandingCount }) =>
    [outstandingCount > 0, assignments.length > 0].every(Boolean),
  complete: ({ outstandingCount }) => outstandingCount === 0,
});

const hasNonEmptyString = (value) => typeof value === "string" && value.trim() !== "";
const hasNullableNonEmptyString = (value) => value === null || hasNonEmptyString(value);

const isValidSetupAssignment = (assignment) => [
  hasNonEmptyString(assignment?.classId),
  hasNonEmptyString(assignment?.displayName),
  hasNullableNonEmptyString(assignment?.teacherId),
  hasNullableNonEmptyString(assignment?.teacherName),
  SETUP_ASSIGNMENT_STATUSES.has(assignment?.status),
].every(Boolean);

const isReconciledSetupSummary = (point, assignments) => {
  const outstandingCount = Number(point.outstandingCount);
  if (![Number.isSafeInteger(outstandingCount), outstandingCount >= 0].every(Boolean)) return false;

  const assignmentState = point.assignmentState;
  if (assignmentState === undefined || assignmentState === null) {
    return assignments.length === outstandingCount;
  }
  if (!Object.prototype.hasOwnProperty.call(SETUP_ASSIGNMENT_STATE_VALIDATORS, assignmentState)) {
    return false;
  }
  const validate = SETUP_ASSIGNMENT_STATE_VALIDATORS[assignmentState];
  return typeof validate === "function" && validate({ assignments, outstandingCount });
};

const setupAssignmentsFor = (point, confirmedClassrooms) => {
  if (point.setupAssignments === undefined || point.setupAssignments === null) {
    if (point.assignmentState !== undefined && point.assignmentState !== null) {
      throw new Error("The classroom setup summary is invalid.");
    }
    return [];
  }
  if (!Array.isArray(point.setupAssignments)) throw new Error("The classroom setup summary is invalid.");

  const confirmedIds = new Set(confirmedClassrooms.map((classroom) => classroom.id));
  const assignmentIds = point.setupAssignments.map((assignment) => assignment?.classId);
  const valid = [
    point.setupAssignments.every(isValidSetupAssignment),
    new Set(assignmentIds).size === assignmentIds.length,
    assignmentIds.every((id) => !confirmedIds.has(id)),
    isReconciledSetupSummary(point, point.setupAssignments),
  ].every(Boolean);
  if (!valid) throw new Error("The classroom setup summary is invalid.");
  return point.setupAssignments;
};

const progressionPointId = (point) => point.progressionPointId || point.id;

// fallow-ignore-next-line complexity -- exercised through loadTask reconciliation tests.
const isReconciliationReady = (point) => {
  if (!point.selected) return false;
  const expected = Number(point.expectedClassroomCount);
  if (!Number.isSafeInteger(expected) || expected < 1) return false;
  if (Number(point.confirmedCount) < expected) return false;
  return point.reconciliationStatus !== "completed";
};

const reconciliationReadyPoints = (state) =>
  (state?.progressionPoints || []).filter(isReconciliationReady);

const withReconciliationChildren = async (state, schoolId, academicYear) => {
  const readyPoints = reconciliationReadyPoints(state);
  if (readyPoints.length === 0) return state;

  const details = await Promise.all(readyPoints.map(async (point) => {
    const pointId = progressionPointId(point);
    return [pointId, await getClassroomTransitionReconciliation(
      schoolId,
      academicYear,
      pointId
    )];
  }));
  const detailsByPoint = new Map(details);
  return {
    ...state,
    progressionPoints: state.progressionPoints.map((point) => ({
      ...point,
      leftoverChildren: detailsByPoint.get(progressionPointId(point))?.leftoverChildren,
    })),
  };
};

export const mapClassroomTransitionState = (
  state,
  { schoolId, academicYear, configuredProgressionPoints = [] }
) => {
  const configuredPoints = normalizeConfiguredProgressionPoints(configuredProgressionPoints);
  const configuredCountsById = new Map(configuredPoints
    .map((point) => [point.id, point.defaultClassroomCount]));
  const configuredCountsByTargetLabel = new Map(configuredPoints
    .map((point) => [point.label.trim(), point.defaultClassroomCount]));
  const points = (state?.progressionPoints || []).map((point) => {
    const id = progressionPointId(point);
    const progressionKey = configuredPoints.find((configuredPoint) =>
      configuredPoint.label.trim() === point.targetClassLevelName?.trim()
    )?.id || id;
    const savedCount = isValidClassroomCount(point.expectedClassroomCount)
      ? point.expectedClassroomCount : null;
    const defaultCount = savedCount || configuredCountsById.get(id) ||
      configuredCountsByTargetLabel.get(point.targetClassLevelName?.trim()) || null;
    const confirmedClassrooms = Array.isArray(point.confirmedClassrooms)
      ? point.confirmedClassrooms
        .filter((classroom) => classroom?.id && classroom?.name)
        .map((classroom) => ({
          id: classroom.id,
          name: classroom.name,
          academicYear: classroom.academicYear,
          grade_level: classroom.grade_level,
        }))
      : [];
    const confirmedCount = Math.max(confirmedClassrooms.length, Number(point.confirmedCount) || 0);
    const unassignedChildren = Array.isArray(point.leftoverChildren)
      ? point.leftoverChildren
        .filter((child) => child?.id && child?.name?.trim())
        .map((child) => ({ id: child.id, name: child.name.trim() }))
      : [];
    const outstandingCount = point.outstandingCount;
    const setupAssignments = setupAssignmentsFor(point, confirmedClassrooms);
    return {
      id,
      progressionKey,
      label: `${point.sourceClassLevelName} → ${point.targetClassLevelName}`,
      defaultClassroomCount: defaultCount,
      classroomCount: savedCount,
      confirmedClassrooms,
      setupAssignments,
      unassignedChildren,
      classroomProgress: savedCount ? {
        expected: savedCount,
        complete: confirmedCount,
        outstanding: outstandingCount,
      } : null,
      progress: pointProgress(point),
      selected: Boolean(point.selected),
    };
  });
  const selected = points.filter((point) => point.selected);
  const now = new Date().toISOString();
  const empty = createEmptyDifferentGroupingsTask({ schoolId, academicYear, now });
  return {
    ...empty,
    transitionRunId: state?.transitionRunId,
    status: state?.status,
    progressStatus: state?.progressStatus,
    scopeStatus: state?.scopeStatus,
    scopeVersion: state?.scopeVersion,
    selectedProgressionPointIds: selected.map((point) => point.id),
    progressionPoints: points,
    archivedAt: null,
    createdAt: now,
    updatedAt: now,
  };
};

export const createDifferentGroupingsApiAdapter = ({ source, launchId, progressionPoints = [] } = {}) => {
  const stagedTasks = new Map();
  const mutationGenerations = new Map();
  let scopeMutationQueue = Promise.resolve();

  const loadState = async (schoolId, academicYear) => {
    const preflight = await getClassroomTransitionPreflight({
      schoolId,
      targetAcademicYear: academicYear,
      source,
      launchId,
    });
    const state = await withReconciliationChildren(preflight, schoolId, academicYear);
    const task = mapClassroomTransitionState(state, {
      schoolId,
      academicYear,
      configuredProgressionPoints: progressionPoints,
    });
    stagedTasks.set(task.taskReference, task);
    return task;
  };

  const staged = (taskReference) => stagedTasks.get(taskReference);
  const markMutation = (taskReference) => {
    const generation = (mutationGenerations.get(taskReference) || 0) + 1;
    mutationGenerations.set(taskReference, generation);
    return generation;
  };
  const selectedPoints = (task) => task.progressionPoints.filter((point) =>
    task.selectedProgressionPointIds.includes(point.id)
  );
  const canPersistScope = (task) => {
    const points = selectedPoints(task);
    return points.length > 0 && points.every((point) => isValidClassroomCount(point.classroomCount));
  };
  const enqueueScopeMutation = (operation) => {
    scopeMutationQueue = scopeMutationQueue.catch(() => {}).then(operation);
    return scopeMutationQueue;
  };
  const persistLatestScope = (taskReference, queuedGeneration) => enqueueScopeMutation(async () => {
    const latest = staged(taskReference);
    const response = await saveClassroomTransitionScope(latest.schoolId, latest.academicYear, selectedPoints(latest).map((point) => ({
      progressionPointId: point.id,
      expectedClassroomCount: point.classroomCount,
    })), latest.scopeVersion);
    const saved = mapClassroomTransitionState(response, {
      ...latest,
      configuredProgressionPoints: progressionPoints,
    });
    if ((mutationGenerations.get(taskReference) || 0) > queuedGeneration) {
      const current = staged(taskReference);
      saved.selectedProgressionPointIds = current.selectedProgressionPointIds;
      saved.progressionPoints = saved.progressionPoints.map((point) => {
        const pending = current.progressionPoints.find((candidate) => candidate.id === point.id);
        return pending ? { ...point, classroomCount: pending.classroomCount, defaultClassroomCount: pending.defaultClassroomCount } : point;
      });
    }
    stagedTasks.set(taskReference, saved);
    return saved;
  });

  return {
    capabilities: Object.freeze({
      nomination: true,
      tcsdHandoff: true,
    }),
    loadTask: loadState,
    checkAvailability: async (schoolId, academicYear) => {
      await loadState(schoolId, academicYear);
      return true;
    },
    saveScope: async (taskReference, selectedIds = []) => {
      const task = staged(taskReference);
      if (!task) throw new Error("Different Groupings task is not loaded.");
      const ids = [...new Set(selectedIds)];
      const next = { ...task, selectedProgressionPointIds: ids };
      stagedTasks.set(taskReference, next);
      const generation = markMutation(taskReference);
      return canPersistScope(next) ? persistLatestScope(taskReference, generation) : next;
    },
    saveClassroomCount: async (taskReference, progressionPointId, count) => {
      if (!isValidClassroomCount(count)) throw new Error("A classroom count must be a positive whole number.");
      const task = staged(taskReference);
      if (!task) throw new Error("Different Groupings task is not loaded.");
      const next = {
        ...task,
        progressionPoints: task.progressionPoints.map((point) => point.id === progressionPointId
          ? { ...point, classroomCount: count, defaultClassroomCount: count } : point),
      };
      stagedTasks.set(taskReference, next);
      const generation = markMutation(taskReference);
      return canPersistScope(next) ? persistLatestScope(taskReference, generation) : next;
    },
    recordTcsdHandoff: async (taskReference) => {
      const task = staged(taskReference);
      if (!task) throw new Error("Different Groupings task is not loaded.");
      const handoff = await issueClassroomTransitionHandoff(task.schoolId, task.academicYear);
      if (!isIssuedHandoff(handoff)) throw new Error("The TCSD handoff response is invalid.");
      const nextTask = { ...task, handoffAttempts: task.handoffAttempts + 1 };
      stagedTasks.set(taskReference, nextTask);
      return { task: nextTask, handoff };
    },
    submitNomination: async (taskReference, fullName, position, schoolEmail) => {
      const task = staged(taskReference);
      if (!task) throw new Error("Different Groupings task is not loaded.");
      const nomination = await submitClassroomTransitionTeacherNomination(task.schoolId, task.academicYear, {
        fullName,
        position,
        schoolEmail,
      });
      if (!nomination || typeof nomination.status !== "string") throw new Error("The nomination response is invalid.");
      return nomination;
    },
  };
};
